import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@kroll-resolver-crisp/ui-components-react';
import type {TaxonomyChange} from '../../api/workflows';

const ORDER: Record<TaxonomyChange['kind'], number> = {
  removed: 0,
  superseded: 1,
  renamed: 2,
  moved: 3,
  added: 4
};

function detail(change: TaxonomyChange) {
  switch (change.kind) {
    case 'superseded': {
      return change.old_name === change.new_name
        ? `Replaced by a new concept (${change.replaced_by?.slice(0, 8)})`
        : `Replaced by ${change.new_name}`;
    }

    case 'renamed': {
      return `Now ${change.new_name}`;
    }

    case 'moved': {
      return 'Moved under a different parent';
    }

    case 'removed': {
      return 'No longer in the taxonomy';
    }

    case 'added': {
      return 'New under this scope';
    }
  }
}

function grounding(change: TaxonomyChange) {
  if (change.grounding_entry_count === 0) {
    return '—';
  }

  const entries = `${change.grounding_entry_count} ${change.grounding_entry_count === 1 ? 'entry' : 'entries'}`;
  return change.kind === 'superseded'
    ? `${entries} carried over`
    : change.kind === 'removed'
      ? `${entries} orphaned`
      : entries;
}

/** How the customer taxonomy has moved since the workflow's approved snapshot. */
export function TaxonomyChanges({changes}: {readonly changes: readonly TaxonomyChange[]}) {
  const rows = [...changes].sort((a, b) => ORDER[a.kind] - ORDER[b.kind]);
  return (
    <div className="border-border overflow-x-auto rounded border">
      <Table aria-label="Taxonomy changes">
        <TableHeader>
          <TableRow>
            <TableHead className="w-28">Change</TableHead>
            <TableHead>Concept</TableHead>
            <TableHead>Detail</TableHead>
            <TableHead className="w-40">Grounding</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((change) => (
            <TableRow key={`${change.kind}:${change.concept_id}`}>
              <TableCell>
                <Badge variant={change.kind === 'removed' ? 'destructive' : 'secondary'}>
                  {change.kind}
                </Badge>
              </TableCell>
              <TableCell>{change.old_name ?? change.new_name}</TableCell>
              <TableCell className="text-muted-foreground text-xs">{detail(change)}</TableCell>
              <TableCell
                className={`text-xs ${change.kind === 'removed' && change.grounding_entry_count > 0 ? 'text-destructive' : ''}`}>
                {grounding(change)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
